import { apiErrorMessage } from '@agos/api-client'
import {
  getGetPaymentsByDriverQueryKey,
  getGetPaymentsQueryKey,
  getPaymentsTestConnection,
  useGetPayments,
  useGetPaymentsUnreconciled,
  useGetPaymentsCount,
  useGetSettingsPayments,
  useGetSettingsPaymentsPublic,
  usePostPaymentsPaymentIdRefund,
  usePostPaymentsSettleStale,
} from '@agos/api-client/payments'
import {
  OrderNumberLink,
  T,
  activeTab,
  cardPayOnlineLabel,
  formatCents,
  formatCount,
  formatDateTime,
  parseMoneyToCents,
  qrPayFigure,
  qrPayLabel,
  stripIsWorthDrawing,
  tt,
  useAuth,
  useFranchiseModules,
  visibleTabs,
} from '@agos/shared'
import {
  cn,
  ConfirmDialog,
  EmptyState,
  FilterSelect,
  PageHeader,
  ReadFailedBlock,
  SearchInput,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  useToast,
} from '@agos/ui'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Info,
  RefreshCw,
  SearchCheck,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'wouter'

import { mayWrite } from '../write-access'
import { DriverPaymentsPanel } from './DriverPaymentsPanel'
import { ARCHIVE_TAB, DRIVERS_TAB, ONLINE_PAYMENTS_TABS } from './online-payments-tabs'
import {
  publicGatewayMessage,
  publicGatewayState,
  publicPaymentFactsOf,
  readsPaymentSettings,
} from './public-gateway'
import {
  PAGE_SIZE,
  METHODS,
  STATUSES,
  connectionTestNote,
  gatewayMessage,
  gatewayName,
  gatewayState,
  archiveNote,
  archiveTotalsOf,
  isTruncated,
  refundBlock,
  refundBlockMessage,
  refundConfirmation,
  rowsOf,
  SEARCH_DEBOUNCE_MS,
  searchRows,
  methodCell,
  methodLabel,
  railNote,
  statusLabel,
  sweepNote,
  sweptOf,
  type PaymentRow,
  unreconciledFigures,
  unreconciledNote,
  unreconciledOf,
  unreconciledTruncation,
} from './payment-archive'

/**
 * `/online-payments` — the card-payment archive, and two checkout rules.
 *
 * **FB-157.** `GET /payments` defaults to `limit = 50`. Legacy passes none, offers no pagination, and computes
 * four stat cards from whatever came back — so on the restored data the cards say 50 transactions and
 * **810,95 €** where the archive holds 1 355 and **26 417,25 €**, a factor of thirty-three. Choosing a status
 * filter narrows the same cards without saying so.
 *
 * **FB-158.** The search filters those fifty rows client-side while the endpoint accepts no search, so an
 * order number from last month reports *nothing found* for a payment that is in the archive.
 *
 * **And the money was a parsed float**, summed across a thousand rows. §14.7: cents.
 *
 * Ported from legacy `pages/online-payments.tsx` (489 lines).
 */

export function OnlinePaymentsPage() {
  const { user, hasPermission } = useAuth()
  const { isModuleEnabled } = useFranchiseModules()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  /* §9.749, owner O121 - *Transakcie* and *Podľa rozvozárov*, *Delivery hub*'s pattern. The per-driver tab is the
   * archive's whole right, so a `_view` holder has one tab, sees no strip and an unchanged page, and a forwarded
   * `?tab=drivers` opens the archive for them - `activeTab`'s rule for a tab the reader cannot see. */
  const [params, setParams] = useSearchParams()
  const tabs = useMemo(
    () =>
      visibleTabs(ONLINE_PAYMENTS_TABS, {
        systemRole: user?.systemRole,
        hasPermission,
        isModuleEnabled,
      }),
    [user?.systemRole, hasPermission, isModuleEnabled],
  )
  const tab = activeTab(tabs, params.get('tab')) ?? ARCHIVE_TAB
  const onDrivers = tab === DRIVERS_TAB

  const [status, setStatus] = useState('')
  // §10A.126, the owner's *"add filter for 'CardPay' and filter for 'QR Pay', so we can filter them
  // out"*. Server-side like the status and the search, so it filters the archive rather than the
  // fifty rows that happened to come back - which is FB-158's finding, one field over.
  const [method, setMethod] = useState('')
  const [search, setSearch] = useState('')
  const [confirming, setConfirming] = useState<PaymentRow | null>(null)
  /* §9.459 - the unreconciled panel starts folded, on the owner's instruction. Shut by default and
   * not remembered: it is a list somebody opens to act on and closes again, so a sticky preference
   * would mean the next visit shows a state nobody chose. */
  const [gapOpen, setGapOpen] = useState(false)

  /* §9.750, FB-1065 - **the gateway's settings for the one role that may read them, the checkout's for everybody
   * else.** `GET /settings/payments` is `@RequireAuth("main_admin")`, so read for a waiter holding
   * `online_payments_view` or a Nesvady administrator it answered 403, the line said the gateway's state could not be
   * read, and both checkout rules were drawn *off*. `GET /settings/payments/public` is what the checkout itself reads,
   * and two of its three flags are those two rules. */
  const readsSettings = readsPaymentSettings(user?.systemRole)
  /* §9.793 - *Otestovať spojenie*, for the one role the endpoint answers (`@RequireAuth("main_admin")`, the same
   * reader as the settings above). It existed with no caller; setting GP webpay up is when somebody needs it. */
  const [testing, setTesting] = useState(false)
  const settings = useGetSettingsPayments({ query: { enabled: readsSettings } })
  const publicSettings = useGetSettingsPaymentsPublic({ query: { enabled: !readsSettings } })

  // FB-158. The search is the server's now, so an order number from last month finds its payment
  // instead of reporting nothing. Debounced the way `/settings-translations` does it: a keystroke is
  // not a query, and this one runs two of them.
  const [query, setQuery] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  const filters = {
    ...(status === '' ? {} : { status }),
    ...(method === '' ? {} : { method }),
    ...(query.trim() === '' ? {} : { search: query.trim() }),
  }
  const list = useGetPayments(Object.keys(filters).length === 0 ? undefined : filters)
  /**
   * The card orders that never reached the bank - §10A.207, FB-764.
   *
   * **The owner found this defect from the order side**, because the order screen was the only place
   * these showed at all: *"its cardapy and we see «Čaká na overenie» but in Platba Online - archív
   * there its not."* The archive lists `online_payments`, and these have no row - the checkout
   * writes the order before it creates the session, so an abandoned CardPay leaves nothing for this
   * screen or the stale sweep to find. **32 of the owner's 61** unsettled card orders are in that
   * state.
   *
   * Unfiltered on purpose: the search and status controls above narrow the *archive*, and narrowing
   * a backlog of unreconciled money by a filter somebody set for another reason is how it stays
   * unnoticed.
   */
  const unreconciled = useGetPaymentsUnreconciled()
  /* FB-963, §9.638 - the rows come through `unreconciledOf` because §10A.322 wrapped the list, and it reads a bare
     array too so a server mid-deploy draws the panel rather than nothing. */
  const gapOrders = useMemo(() => unreconciledOf(unreconciled.data), [unreconciled.data])
  /* FB-963 - **the count and the money come off the server, not off the page.** All three claims this panel makes
     were reduced from whatever page arrived, and the server caps it at fifty: measured at 38 of 50 at Šurany on a
     set that never shrinks, so one gateway outage put the figure past the cap. The reduction survives inside
     `unreconciledFigures` as the fallback for an older backend. */
  const gapFigures = useMemo(
    () => unreconciledFigures(unreconciled.data, gapOrders),
    [unreconciled.data, gapOrders],
  )
  const gapTruncated = unreconciledTruncation(gapOrders.length, gapFigures)
  // FB-157. What the archive holds under the same filters — the cards were computed from whatever the
  // last request returned, so they said 50 transactions and 810,95 € against 1 355 and 26 417,25 €.
  const counted = useGetPaymentsCount(Object.keys(filters).length === 0 ? undefined : filters)

  const rows = useMemo(() => rowsOf(list.data), [list.data])
  const archive = useMemo(() => archiveTotalsOf(counted.data), [counted.data])
  // Local narrowing only while the debounced request is in flight, so typing stays immediate; the
  // server's answer replaces it the moment it lands.
  const visible = useMemo(
    () => (query.trim() === search.trim() ? [...rows] : searchRows(rows, search)),
    [rows, search, query],
  )
  const truncated = isTruncated(rows, archive)

  /* §9.749 - *Obnoviť* and a successful *Overiť čakajúce* refresh **both** reads. The two keys do not share a prefix
   * (`/api/v1/payments` against `/api/v1/payments/by-driver`), so one invalidation would leave the per-driver check
   * reading an answer from before the sweep that just settled its payments. */
  const refreshBothReads = () => {
    void queryClient.invalidateQueries({ queryKey: getGetPaymentsQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getGetPaymentsByDriverQueryKey() })
  }
  const driverReadsInFlight = useIsFetching({ queryKey: getGetPaymentsByDriverQueryKey() })
  const refreshing = onDrivers ? driverReadsInFlight > 0 : list.isFetching

  /* §9.750 - the public answer's seam passes a failure as null, exactly as the staff one below does, so a failed read
     cannot reach the line as an absent payload. *Pravidlá pokladne* left this page with §9.766 (owner O127): they are
     on *Nastavenia rozvozu → Platby zákazníkov* with the two switches that decide which ways a customer may pay. */
  const publicFacts = publicSettings.isError ? null : publicPaymentFactsOf(publicSettings.data)
  // Spread: `gatewayState` reads a bag by key, and the payload is typed now (BLOCKED #4). Widening here keeps that
  // reader tolerant without a cast.
  /* FB-817, §9.538 - **null for a failed read, not the spread of an absent payload.** `{ ...undefined }` is
     `{}`, which reached `_hasClientSecret !== true` and printed *the gateway is not configured* on a live
     installation. The seam is the argument: dropping it here compiles and restores the defect, so a case asserts
     this call site as well as the helper - §9.518's shape, where an optional argument went missing at the only
     call site in the frontend. */
  const gateway = gatewayState(settings.isError ? null : { ...settings.data })
  const gatewayKey = settings.data?.gateway ?? 'tatrapay'
  const publicGateway = publicGatewayState(publicFacts)

  /* §9.758, BACKEND.md §10A.413 - the stale sweep (`POST /payments/settle-stale`) asks `online_payments`, the right
     `PUT /settings/payments` asks for the checkout preferences. Those switches left this page with §9.766, so the gate
     is the sweep's alone; the gateway's own fields stay the main administrator's and this page never sends them. */
  const maySweep = mayWrite('payments', user?.systemRole, hasPermission)

  const refund = usePostPaymentsPaymentIdRefund({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetPaymentsQueryKey() })
        toast({ title: tt('Platba vrátená') })
      },
      // §9.355, FB-635 - **this sentence used to be printed for all six refusals**, five of which
      // are not the bank. The damaging one is a transfer, where the server answers with an
      // *instruction* - refund it manually from the account - and the screen replaced it with a
      // claim that the bank said no, so the operator retries a button that can never work.
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: tt('Vrátenie zlyhalo'),
          description: apiErrorMessage(
            error,
            tt('Banka alebo brána ho odmietla - v archíve sa nič nezmenilo.'),
          ),
        }),
    },
  })

  /**
   * Ask the bank about the card payments nobody came back from, now - §10A.194.
   *
   * **Why a button exists for something a job does every minute.** The sweep lives in
   * `ScheduledJobs`, which is `@ConditionalOnProperty("agos.jobs.enabled")` - false on every
   * developer stack and in the whole e2e, so wherever that flag is off the rule has no caller at
   * all. Where the handover has happened, this is still what answers *is it working* without
   * reading a log over ssh, and what clears a backlog after an outage instead of trickling through
   * it at one pass a minute.
   *
   * **It reports the five counts rather than saying `Hotovo`.** The sweep changes orders - paid,
   * refunded, failed - so a bare confirmation would tell somebody who just pressed a button that
   * touches money nothing about what it touched.
   */
  const settleStale = usePostPaymentsSettleStale({
    mutation: {
      onSuccess: (result: unknown) => {
        const swept = sweptOf(result)
        refreshBothReads()
        toast({
          title: tt('Overenie platieb'),
          // An unreadable answer is not a quiet sweep and must not print as one.
          description: swept === null ? tt('Server odpovedal v neznámom tvare.') : sweepNote(swept),
          ...(swept === null ? { variant: 'destructive' as const } : {}),
        })
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: apiErrorMessage(error, tt('Platby sa nepodarilo overiť.')),
          variant: 'destructive',
        }),
    },
  })

  return (
    <div className="space-y-5" data-online-payments={tab}>
      <PageHeader
        title={tt('Online platby')}
        description={tt('Archív kartových platieb')}
        icon={<CreditCard className="w-5 h-5" />}
        actions={
          <div className="flex items-center gap-2">
          {maySweep && (
            <button
              type="button"
              data-settle-stale
              disabled={settleStale.isPending}
              onClick={() => settleStale.mutate()}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-card text-sm text-muted-foreground disabled:opacity-50"
            >
              <SearchCheck className={cn('w-4 h-4', settleStale.isPending && 'animate-pulse')} />{' '}
              <T>Overiť čakajúce</T>
            </button>
          )}
          <button
            type="button"
            data-refresh
            disabled={refreshing}
            onClick={refreshBothReads}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-card text-sm text-muted-foreground disabled:opacity-50"
          >
            <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} /> <T>Obnoviť</T>
          </button>
          </div>
        }
      />

      {/* One tab is not a choice, and a strip of one implies a second this reader cannot have. The tab is in the URL,
          so *Platby online → Podľa rozvozárov* is something one person can send another. */}
      {stripIsWorthDrawing(tabs) && (
        <div data-online-payments-tabs>
          <TabGroup
            variant="bar"
            tabs={tabs.map((entry) => ({ id: entry.id, label: entry.label }))}
            active={tab}
            onChange={(id) => {
              const next = new URLSearchParams(params)
              next.set('tab', id)
              setParams(next, { replace: true })
            }}
            // §9.749 - a thumb's 44 px on a phone; the strip scrolls inside itself there rather than the page.
            className="*:min-h-11 sm:*:min-h-0"
          />
        </div>
      )}

      {onDrivers ? (
        <DriverPaymentsPanel />
      ) : (
        <>
      {/* §9.750 - drawn once the read this viewer makes has answered or failed: before that, an absent payload reads
          as *not configured*, which is a claim about the gateway made from a request still in flight. */}
      {readsSettings ? (
        settings.isPending ? null : (
          <p
            className={cn(
              'text-xs flex items-start gap-2',
              gateway === 'live' ? 'text-muted-foreground' : 'text-amber-500',
            )}
            data-gateway={gateway}
          >
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span className="flex-1">{gatewayMessage(gateway, gatewayKey)}</span>
            {gateway !== 'unknown' && (
              <button
                type="button"
                data-gateway-test
                disabled={testing}
                onClick={() => {
                  setTesting(true)
                  void getPaymentsTestConnection()
                    .then((result) => {
                      const note = connectionTestNote(result, gatewayKey)
                      toast({
                        title: note.text,
                        ...(note.ok ? {} : { variant: 'destructive' as const }),
                      })
                    })
                    .catch((failure: unknown) =>
                      toast({
                        title: apiErrorMessage(failure, tt('Spojenie sa nepodarilo overiť')),
                        variant: 'destructive',
                      }),
                    )
                    .finally(() => setTesting(false))
                }}
                className="shrink-0 px-2 py-0.5 rounded-lg border border-border text-[11px] font-semibold text-foreground disabled:opacity-50"
              >
                <T>Otestovať spojenie</T>
              </button>
            )}
          </p>
        )
      ) : publicSettings.isPending ? null : (
        <p
          className={cn(
            'text-xs flex items-start gap-2',
            publicGateway === 'usable' ? 'text-muted-foreground' : 'text-amber-500',
          )}
          data-gateway={publicGateway}
        >
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {publicGatewayMessage(publicGateway)}
        </p>
      )}

      {/* FB-157. What the figures describe, before the figures. */}
      <p
        className="text-xs text-muted-foreground flex items-start gap-2"
        data-window={archive === null ? 'unknown' : truncated ? 'page' : 'all'}
      >
        <AlertTriangle className={cn('w-3.5 h-3.5 shrink-0 mt-0.5', truncated && 'text-amber-500')} />
        {archiveNote(rows.length, archive)}
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Counted by the server across the whole archive, not summed over the page. */}
        <StatCard
          label={tt('Transakcií')}
          value={archive === null ? '-' : formatCount(archive.total)}
          icon={CreditCard}
        />
        <StatCard
          label={statusLabel('paid')}
          value={archive === null ? '-' : formatCount(archive.paidCount)}
          color="emerald"
        />
        <StatCard
          label={statusLabel('failed')}
          value={archive === null ? '-' : formatCount(archive.failedCount)}
          color="red"
        />
        <StatCard
          label={tt('Objem zaplatených')}
          value={archive === null ? '-' : formatCents(archive.paidCents)}
          color="blue"
        />
        {/* §9.767, owner O129 - the paid volume apart: CardPay online and QR Pay side by side, by what each payment was
          * opened as (BACKEND.md §10A.419), so a door QR paid by card is QR Pay here as in the chips below. `-` until
          * the count answers, and from a server that does not send them. */}
        <StatCard
          label={cardPayOnlineLabel()}
          value={archive === null || archive.paidCardPayCents === null ? '-' : formatCents(archive.paidCardPayCents)}
          sub={tt('zaplatené')}
          color="violet"
          testId="archive-paid-cardpay-online"
        />
        <StatCard
          label={qrPayLabel()}
          value={archive === null || archive.paidQrPayCents === null ? '-' : qrPayFigure(archive.paidQrPayCents)}
          sub={tt('zaplatené')}
          color="pink"
          testId="archive-paid-qr-pay"
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect
          label={tt('Stav')}
          value={status}
          onChange={setStatus}
          options={[
            { value: '', label: tt('Všetky') },
            ...STATUSES.map((entry) => ({ value: entry, label: statusLabel(entry) })),
          ]}
        />
        {/* §10A.126 - the count comes off the same scan as the statuses, so the label can say how
          * many there are without a second request. */}
        <FilterSelect
          label={tt('Spôsob')}
          value={method}
          onChange={setMethod}
          options={[
            { value: '', label: tt('Všetky') },
            ...METHODS.map((entry) => ({
              value: entry,
              label:
                archive === null
                  ? methodLabel(entry)
                  : `${methodLabel(entry)} (${entry === 'card_pay' ? archive.cardPayCount : archive.qrPayCount})`,
            })),
          ]}
        />
        <div className="min-w-[220px]">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={tt('Číslo objednávky alebo referencia')}
          />
        </div>
      </div>

      {/* §10A.207, FB-764 - above the archive, because it is about the money the archive cannot
        * show. Drawn only when there is something in it: a panel reading *every payment has a
        * session* on every visit is a panel people stop reading.
        *
        * §9.459 - **and folded shut**, on the owner's instruction: *"In platby online - archív,
        * kartové platby bez relácie - make sure that will be visible only when you click on some
        * button"*. The heading stays a heading and becomes the button, so the count and the total -
        * the two facts that say whether it is worth opening - are still on the screen. Hiding those
        * too would make the panel invisible rather than quiet, and nobody would ever click it. */}
      {/* FB-817, §9.538 - the panel is behind `length > 0`, so on a failed read it was **absent**, and on this
          screen that reads as *every card payment has its session*. It exists because the owner reported the
          opposite - *"in Platba Online - archív there its not"* - and 32 of their 61 unsettled card orders were in
          that state, so silence is the one answer it must not give. */}
      {unreconciled.isError ? (
        <section
          className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-3 space-y-1"
          data-unreconciled
          data-unreconciled-unreadable
        >
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-500">
            <T>Kartové platby bez relácie</T>
          </p>
          <p className="text-[11px] text-muted-foreground">
            <T>Zoznam sa nepodarilo načítať, takže nevieme, či nejaké sú.</T>
          </p>
          <button
            type="button"
            onClick={() => void unreconciled.refetch()}
            data-unreconciled-retry
            className="px-2 py-0.5 rounded-lg border border-border text-[11px] font-semibold text-foreground"
          >
            <T>Skúsiť znova</T>
          </button>
        </section>
      ) : null}
      {gapOrders.length > 0 && (
        <section
          className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-3 space-y-2"
          data-unreconciled
        >
          <button
            type="button"
            onClick={() => setGapOpen((open) => !open)}
            aria-expanded={gapOpen}
            data-unreconciled-toggle
            className="flex w-full flex-wrap items-baseline justify-between gap-2 text-left"
          >
            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-500 flex items-center gap-1">
              {gapOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              <T>Kartové platby bez relácie</T>
              <span className="tabular-nums" data-unreconciled-count>
                ({formatCount(gapFigures.count)})
              </span>
            </p>
            <p className="text-[11px] font-bold tabular-nums" data-unreconciled-total>
              {formatCents(gapFigures.cents)}
            </p>
          </button>
          {gapOpen && (
          <p className="text-[11px] text-muted-foreground" data-unreconciled-note>
            {unreconciledNote(gapFigures.count)}
          </p>
          )}
          {/* FB-963, §9.638 - the panel lists the page and the figures above it cover every matching order, so it
            * has to say which rows are missing. Drawn rather than only computed: a figure published, parsed and
            * rendered by nothing is §3.122's shape, which this repository has found six times. */}
          {gapOpen && gapTruncated !== '' && (
            <p className="text-[11px] font-semibold text-amber-600 dark:text-amber-500" data-unreconciled-truncated>
              {gapTruncated}
            </p>
          )}
          {gapOpen && (
          <div className="space-y-1">
            {gapOrders.map((order) => (
              <div
                key={order.id}
                data-unreconciled-order={String(order.id ?? 0)}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs"
              >
                <span className="font-semibold">#{order.orderNumber}</span>
                <span className="tabular-nums">{formatCents(parseMoneyToCents(order.total ?? 0))}</span>
                {order.customerName && (
                  <span className="text-muted-foreground">{order.customerName}</span>
                )}
                {/* The phone, because deciding about one of these means ringing them and asking
                  * whether they paid. Without it the operator goes back to the orders screen, which
                  * is where they already were. */}
                {order.customerPhone && (
                  <a
                    href={`tel:${order.customerPhone}`}
                    className="text-primary"
                    data-unreconciled-phone={String(order.id ?? 0)}
                  >
                    {order.customerPhone}
                  </a>
                )}
                {order.createdAt && (
                  <span className="text-muted-foreground">{formatDateTime(order.createdAt)}</span>
                )}
              </div>
            ))}
          </div>
          )}
        </section>
      )}

      {/* FB-817, §9.538 - *Také číslo objednávky ani referenciu archív neobsahuje* is **evidential**: it says the
          archive was searched. Said to whoever is on the phone to a customer about a payment, so the failure
          takes the first arm of the same chain rather than sitting beside it. */}
      {list.isError ? (
        <ReadFailedBlock
          marker="archive"
          title={tt('Archív platieb sa nepodarilo načítať')}
          onRetry={() => void list.refetch()}
        >
          <T>Neznamená to, že platba neexistuje - len sme sa k zoznamu nedostali.</T>
        </ReadFailedBlock>
      ) : list.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<CreditCard className="w-10 h-10" />}
          title={tt('Žiadna platba')}
          description={
            search.trim() === ''
              ? tt('V tomto výbere nie je žiadna transakcia.')
              : tt('Také číslo objednávky ani referenciu archív neobsahuje.')
          }
        />
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs text-muted-foreground uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">#</th>
                  <th className="text-left px-4 py-3 font-bold">
                    <T>Č. obj.</T>
                  </th>
                  <th className="text-right px-4 py-3 font-bold">
                    <T>Suma</T>
                  </th>
                  <th className="text-left px-4 py-3 font-bold">
                    <T>Stav</T>
                  </th>
                  {/* §10A.126 - the owner asked for the two to be distinguishable, so the row says
                    * which it was rather than only the filter knowing. */}
                  <th className="text-left px-4 py-3 font-bold">
                    <T>Spôsob</T>
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    TransactionID
                  </th>
                  <th className="text-left px-4 py-3 font-bold">
                    <T>Vytvorená</T>
                  </th>
                  <th className="text-right px-4 py-3 font-bold" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visible.map((row) => {
                  const block = refundBlock(row)
                  return (
                    <tr
                      key={row.id}
                      data-payment={row.id}
                      data-status={row.status}
                      data-method={row.method ?? ''}
                      data-order={row.orderNumber ?? ''}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{row.id}</td>
                      {/* §9.143 - a payment ledger; the order it paid for is one click away. */}
                      <td className="px-4 py-3 font-mono text-xs">
                        {row.orderNumber === null ? (
                          '-'
                        ) : (
                          <OrderNumberLink
                            orderId={row.orderId}
                            className="text-primary hover:underline"
                            title={`${tt('Otvoriť v archíve')}: ${row.orderNumber}`}
                          >
                            {row.orderNumber}
                          </OrderNumberLink>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold" data-amount>
                        {formatCents(row.amountCents)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          label={statusLabel(row.status)}
                          color={
                            row.status === 'paid'
                              ? 'emerald'
                              : row.status === 'failed'
                                ? 'red'
                                : row.status === 'refunded'
                                  ? 'blue'
                                  : 'zinc'
                          }
                        />
                      </td>
                      {/* A row the gateway never named a method for reads *neuvedené* rather than
                        * blank: nothing was reported, which is a different fact from *by card*. */}
                      <td className="px-4 py-3 text-xs" data-method-cell>
                        {row.method === null ? (
                          <span className="text-muted-foreground">{methodCell(row)}</span>
                        ) : (
                          <span className="font-semibold">{methodLabel(row.method)}</span>
                        )}
                        {/* §10A.419, owner O129 - a door QR paid by card is QR Pay above and *zaplatené kartou* here,
                          * so the refund a card allows still makes sense. */}
                        {railNote(row) !== '' && (
                          <span className="block text-[11px] text-muted-foreground" data-rail-note={row.rail ?? ''}>
                            {railNote(row)}
                          </span>
                        )}
                      </td>
                      {/* §9.140 - the reference the bank knows the payment by. When a customer says
                        * they were charged and the order says otherwise, this is what gets quoted;
                        * an archive that cannot produce it sends somebody to the database. A UUID is
                        * copied rather than read, so it is selectable with the whole value on hover. */}
                      <td className="px-4 py-3">
                        <span
                          data-transaction={row.id}
                          title={row.transactionId ?? ''}
                          className="block font-mono text-[10px] text-muted-foreground truncate max-w-[130px] select-all"
                        >
                          {row.transactionId ?? '-'}
                        </span>
                        {/* §9.793 - the gateway that took it, which is the one a refund goes through. */}
                        <span
                          className="block text-[10px] text-muted-foreground"
                          data-payment-gateway={row.gateway === '' ? 'tatrapay' : row.gateway}
                        >
                          {gatewayName(row.gateway)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {row.createdAt === '' ? '-' : formatDateTime(row.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {block === null ? (
                          <button
                            type="button"
                            data-refund={row.id}
                            disabled={refund.isPending}
                            onClick={() => setConfirming(row)}
                            className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium disabled:opacity-50"
                          >
                            <T>Vrátiť</T>
                          </button>
                        ) : (
                          <span className="text-[11px] text-muted-foreground" data-refund-blocked={row.id}>
                            {refundBlockMessage(block)}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {truncated && (
            <p className="px-4 py-3 text-[11px] text-muted-foreground border-t border-border" data-page-note>
              {/* No *load more*: the endpoint pages by offset and legacy had no control at all. What is
                  different is that the count is real, so this states a page of a known whole rather than
                  a page of an unknown one. */}
              <T>Zobrazených je najnovších</T> {formatCount(PAGE_SIZE)} <T>z</T>{' '}
              {formatCount(archive?.total ?? 0)} <T>transakcií.</T>
            </p>
          )}
        </div>
      )}

      {/* §9.766, owner O127 - *Pravidlá pokladne* moved to *Nastavenia rozvozu*, beside the two switches that decide
          which ways a customer may pay. One line saying where, for whoever looks for them here. */}
      <p className="text-xs text-muted-foreground" data-customer-payments-moved>
        <T>Pravidlá pokladne a spôsoby platby zákazníkov sú v</T>{' '}
        <Link href="/delivery-settings?tab=customer-payments" className="text-primary underline">
          <T>Nastavenia rozvozu → Platby zákazníkov</T>
        </Link>
        .
      </p>
        </>
      )}

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        variant="danger"
        title={tt('Vrátiť platbu?')}
        description={confirming === null ? '' : refundConfirmation(confirming)}
        confirmLabel={tt('Vrátiť')}
        icon={<RefreshCw className="w-5 h-5" />}
        loading={refund.isPending}
        onConfirm={() => {
          const row = confirming
          setConfirming(null)
          if (row !== null) refund.mutate({ paymentId: row.id })
        }}
      />
    </div>
  )
}
